import type { GridProps, MotionPropsFor, StackProps } from '@hover/blueprint';
import {
  Box,
  Center,
  HStack,
  VStack,
  calc,
  cssVar,
  cssVariables,
  forwardRef,
  motion,
  transition,
  useBreakpointValue,
  useTheme,
} from '@hover/blueprint';
import { assertUnreachable } from '@hover/utilities';
import { AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { CustomizeDesignIdeas } from 'src/modules/homeownerApp/components/Customize/CustomizeDesignIdeas';
import { CustomizeDesignIdeasGallery } from 'src/modules/homeownerApp/components/Customize/CustomizeDesignIdeasGallery';
import { CustomizeEmbedHeading } from 'src/modules/homeownerApp/components/Customize/CustomizeEmbedHeading';
import { DarkThemeColor } from 'src/modules/homeownerApp/components/meta/DarkThemeColor';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Zoom } from 'src/shared/components/Zoom';
import { Route, generatePath } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { fadePresence } from 'src/shared/utils/motion';
import { hideScrollbars } from 'src/shared/utils/styles';

import { usePartnerCobranding } from '../../hooks/usePartner';
import { CobrandLogo } from '../CobrandLogo/CobrandLogo';
import { getFullscreenImageLayoutId } from '../FullscreenImage/helpers';
import { TileButtonPictogram } from '../TileButton';

import { CustomizeActions } from './CustomizeActions';
import {
  CustomizeLayout,
  CustomizeLayoutImage,
  CustomizeLayoutMotion,
  CustomizeLayoutPanel,
} from './CustomizeLayout';
import { CustomizeMaterial } from './CustomizeMaterial';
import { CustomizeMobileCallout } from './CustomizeMobileCallout';
import { CustomizeMobileHeading } from './CustomizeMobileHeading';
import { CustomizePaint } from './CustomizePaint';
import { CustomizeHistory } from './History';
import { useCustomizeFooterDimensions } from './History/CustomizeHistoryFooter';
import { $paddingX } from './History/constants';
import type { MenuItem } from './menu';
import { categoryToRouteParams } from './menu';
import { isCobrandCategory } from './partners';
import { useCustomization } from './useCustomization';
import { useCustomizeReferrerState } from './useCustomizeReferrerState';
import { useImageTracking } from './useImageTracking';

export interface CustomizeProps extends GridProps {
  menu: MenuItem[];
  isRegionsLoading: boolean;
  allowLoadingOverlay: boolean;
  /**
   * isInspire route uses sample images and is a part of the embed flow
   */
  isInspire: boolean;
}

const $paddingTop = cssVariables.space(700);
const $paddingBottom = cssVariables.space(700);
const $gap = cssVariables.space(600);

const $actionsHeight = cssVar('customize-actions-height');
const $actionsGap = cssVar('customize-actions-gap');

const MENU_HEIGHT = '150px';
const MENU_HEIGHT_COBRANDED = '180px';

const $menuHeight = cssVar('customize-menu-height');

const MotionCenter = motion(Center);
const MotionHStack = motion(hideScrollbars(HStack));
const MotionCustomizeActions = motion(CustomizeActions);
const MotionCustomizeMobileHeading = motion(CustomizeMobileHeading);

interface CustomizeMenuProps
  extends MotionPropsFor<StackProps>,
    Pick<CustomizeProps, 'menu'> {
  isLoading: boolean;
}

const CustomizeMenu = forwardRef<CustomizeMenuProps, 'div'>(
  ({ isLoading, menu: menuProp, ...props }, ref) => {
    const { targetImage: image } = useCustomization();
    const state = useCustomizeReferrerState();
    const { trackingData: contextTrackingData } = useHomeownerAppContext();
    const imageTracking = useImageTracking();
    const menu = isLoading ? menuProp.slice(0, 3) : menuProp;

    const cobranding = usePartnerCobranding();

    return (
      <Box justifyContent="center" width="100%">
        <MotionHStack
          alignItems="flex-start"
          data-testid="customize-menu"
          display={{ base: 'none', tablet: 'flex' }}
          flexDirection="row"
          justifyContent="start"
          layout
          overflowX="auto"
          px={{ base: 400, tablet: 0 }}
          py="500"
          spacing="200"
          whiteSpace="nowrap"
          width="fit-content"
          {...fadePresence}
          {...props}
          ref={ref}
        >
          {menu.map(item => (
            <VStack data-testid="customize-menu-item" key={item.label}>
              <TileButtonPictogram
                as={Link}
                colorScheme="dark"
                disabled={isLoading}
                icon={item.icon}
                isLoading={isLoading}
                label={item.label}
                onClick={() => {
                  buttonPressed({
                    ...contextTrackingData,
                    ...imageTracking,
                    button_text: item.label,
                    primary_cta: false,
                    track_id: 'edit-category',
                  });
                }}
                pictogram={item.pictogram}
                textDecoration="none"
                textSize={200}
                to={{
                  pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
                    aiDisplayImageId: image.id,
                    ...categoryToRouteParams(item.category),
                  }),
                  state,
                }}
                width={{ base: '72px', tablet: '90px' }}
              />
              {cobranding &&
                isCobrandCategory(cobranding.type, item.category) && (
                  <CobrandLogo colorMode="dark" hideHoverMark />
                )}
            </VStack>
          ))}
        </MotionHStack>
      </Box>
    );
  },
);

const Customize = ({
  menu,
  isRegionsLoading,
  allowLoadingOverlay,
  isInspire,
  sx,
  ...props
}: CustomizeProps) => {
  const { category, targetImage, baseImage } = useCustomization();

  const { space, sizes } = useTheme();

  const [isImageAnimated, setIsImageAnimated] = useState(false);
  const [zoomableImage, setZoomableImage] = useState<string | null>(null);
  const [exitingZoom, setExitingZoom] = useState(false);

  // Enable layout animations on the customized image for the route transition
  // from the top-level menu to the category menus
  if (!category && !isImageAnimated) setIsImageAnimated(true);

  const handleLayoutAnimationComplete = () => {
    // Once the route animation is complete, disable explicit layout animations
    // on the customized image (and allow it to manage its own animation state)
    if (category) setIsImageAnimated(false);
  };

  const isMobile = useBreakpointValue(
    { base: true, tablet: false },
    { fallback: 'tablet' },
  );

  const shouldShowInspireGallery = isInspire && isMobile;

  const cobranding = usePartnerCobranding();

  let sortedMenu;
  if (cobranding?.type) {
    sortedMenu = menu.sort((a, b) => {
      if (a.category.region === cobranding?.type) return -1;
      if (b.category.region === cobranding?.type) return 1;
      return 0;
    });
  }

  const isMenuCobranded =
    !!cobranding &&
    menu.some(item => isCobrandCategory(cobranding.type, item.category));

  const { offset: footerOffset } = useCustomizeFooterDimensions();

  const handleCloseZoom = () => {
    setExitingZoom(true);
    setZoomableImage(null);
  };

  const handleZoomOnClick = (imageToZoom?: string) => {
    if (imageToZoom) {
      setZoomableImage(imageToZoom);
    }
  };

  const isTwoColumn = category || isInspire;
  // Max width is hardcoded for the embed to optimize for the iframe max height
  // since we are using a 1:1 aspect ratio on tiles within customize controls.
  const twoColumnGridTemplateColumnsStyle = baseImage.fromSample
    ? `auto minmax(480px, 580px)`
    : 'minmax(40%, auto) minmax(40%, 0.75fr)';

  return (
    <CustomizeLayout
      background="neutral.900"
      fromSample={baseImage.fromSample}
      gridTemplateColumns={
        isTwoColumn
          ? { tablet: twoColumnGridTemplateColumnsStyle }
          : { tablet: '100% 0%' }
      }
      gridTemplateRows={
        category ? undefined : { tablet: calc.subtract('100%', $menuHeight) }
      }
      sx={{
        ...sx,
        [$actionsHeight.variable]: {
          base: sizes[300],
          tablet: category || isInspire ? '0px' : sizes[400],
        },
        [$actionsGap.variable]: {
          base: space[300],
          tablet: category ? '0px' : space[500],
        },
        [$menuHeight.variable]: isMenuCobranded
          ? MENU_HEIGHT_COBRANDED
          : MENU_HEIGHT,
      }}
      {...props}
    >
      <DarkThemeColor />
      <CustomizeLayoutMotion>
        {zoomableImage && (
          <Zoom
            backgroundColor="neutral.900"
            bottom={baseImage.fromSample ? 900 : 0}
            fullScreen
            image={zoomableImage}
            onCloseClick={() => handleCloseZoom()}
          />
        )}
        <CustomizeLayoutImage
          flexDirection="column"
          justifyContent="space-between"
          overflow={!shouldShowInspireGallery ? 'hidden' : undefined}
          pb={{ base: 0, tablet: $paddingBottom.reference }}
          pt={{
            base: `calc(env(safe-area-inset-top, 0px) + ${space[300]})`,
            tablet: $paddingTop.reference,
          }}
          px={$paddingX.reference}
          sx={{
            [$paddingX.variable]: { base: space[500], tablet: space[800] },
          }}
          zIndex={1}
        >
          <AnimatePresence initial={false} mode="popLayout">
            <CustomizeEmbedHeading />

            {!baseImage.fromSample &&
              (category ? (
                <MotionCustomizeMobileHeading
                  category={category}
                  display={{ base: 'grid', tablet: 'none' }}
                  flexShrink={0}
                  height={$actionsHeight.reference}
                  key="heading"
                  mb={$actionsGap.reference}
                  width="100%"
                  {...fadePresence}
                />
              ) : (
                <MotionCenter
                  alignSelf="stretch"
                  height={$actionsHeight.reference}
                  key="actions"
                  mb={$actionsGap.reference}
                  {...fadePresence}
                >
                  <MotionCustomizeActions width="100%" />
                </MotionCenter>
              ))}
          </AnimatePresence>
          <Center
            flexDirection="column"
            gap={{ base: 0, tablet: $gap.reference }}
            height={calc.subtract(
              '100%',
              $actionsHeight,
              $actionsGap,
              footerOffset,
            )}
            justifyContent={shouldShowInspireGallery ? 'start' : 'space-around'}
            position="relative"
            transform={{
              base: `translateY(${calc.multiply(footerOffset, -1)})`,
              tablet: 'none',
            }}
            {...(category && !isImageAnimated
              ? transition.easing500('height')
              : {})}
            width="100%"
          >
            <MotionCenter
              alignItems="center"
              display={shouldShowInspireGallery ? 'none' : 'flex'}
              flexDirection="column"
              flexGrow={1}
              height="100%"
              justifyContent="start"
              layout
              onLayoutAnimationComplete={handleLayoutAnimationComplete}
              transform={isImageAnimated ? undefined : 'none !important'}
              width="100%"
            >
              <CustomizeHistory
                display={shouldShowInspireGallery ? 'none' : 'flex'}
                exitingZoom={exitingZoom}
                isLayoutAnimated={isImageAnimated}
                layoutId={getFullscreenImageLayoutId(targetImage)}
                setExitingZoom={() => setExitingZoom(false)}
                showLoadingOverlay={isRegionsLoading && allowLoadingOverlay}
                zoomOnClick={(imageToZoom?: string) =>
                  handleZoomOnClick(imageToZoom)
                }
              />
            </MotionCenter>
            {shouldShowInspireGallery && <CustomizeDesignIdeasGallery />}
          </Center>
        </CustomizeLayoutImage>
        <CustomizeLayoutPanel
          bg={{ base: 'transparent', tablet: 'neutral.0' }}
          fromSample={baseImage.fromSample}
          layout
          shadow="distance400"
          zIndex={2}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {category ? (
              (() => {
                switch (category.type) {
                  case 'paint':
                    return <CustomizePaint key="paint" {...fadePresence} />;
                  case 'material':
                    return (
                      <CustomizeMaterial
                        key="material"
                        region={category.region}
                        spaceType={targetImage.spaceType ?? 'exterior'}
                        {...fadePresence}
                      />
                    );
                  default:
                    return assertUnreachable(category);
                }
              })()
            ) : (
              <CustomizeMenu
                display={{ base: 'flex', tablet: 'none' }}
                isLoading={isRegionsLoading}
                key="menu"
                menu={sortedMenu ?? menu}
                {...fadePresence}
              />
            )}
          </AnimatePresence>
          {isInspire && !isMobile && <CustomizeDesignIdeas />}
          <CustomizeMobileCallout
            display={{ base: 'flex', tablet: 'none' }}
            flexShrink={0}
          />
        </CustomizeLayoutPanel>
        <AnimatePresence mode="popLayout">
          {!category && (
            <CustomizeMenu
              alignItems="start"
              display={{ base: 'none', tablet: 'flex' }}
              height={$menuHeight.reference}
              isLoading={isRegionsLoading}
              layout={false}
              menu={sortedMenu ?? menu}
              py={0}
              {...fadePresence}
            />
          )}
        </AnimatePresence>
      </CustomizeLayoutMotion>
    </CustomizeLayout>
  );
};

export { Customize };
